import { Injectable, NgZone } from '@angular/core';
import { openDB, deleteDB, wrap, unwrap, IDBPDatabase } from 'idb';
import {
    Operation,
    SalesDocument,
    SalesDocumentPosition,
    SalesDocumentConnection,
    DocumentAttachment,
    SalesDocumentConnections,
    dbVersion,
    SerializedClock,
    operationIndexFunc,
    IccDocument,
    IccDocumentPosition,
    Delivery,
    ComplaintAppointment,
    ComplaintSparePart,
    ComplaintNote,
    OpenedDocument,
} from '@icc/b2b/shared';
import { ConfigService, logger } from '@icc/shared';
import { ToastService } from '@icc/shared/helpers';
import { TranslateService } from '@ngx-translate/core';
import { UniqueSelectionDispatcher } from '@angular/cdk/collections';
import { IDBService } from './idb.service';

@Injectable({
    providedIn: 'root',
})
export class IDBServiceBrowser extends IDBService {

    constructor(
        private ngZone: NgZone,
        private toastService: ToastService,
        private translateService: TranslateService,
        private configService: ConfigService,
    ) {
        super();
    }

    async open(user, envDbVersion: number) {
        await super.open(user, envDbVersion, () => {
            if (this.configService.user?.id) {
                this.toastService.add({
                    title: this.translateService.instant('INTERFACE|Aktualizacja bazy danych'),
                    body: this.translateService.instant('INTERFACE|Baza danych aplikacji została zaktualizowana.'),
                });
            }
        });

        this.dropStaleDatabases(user, envDbVersion);
        (window as any).merkleAll = this.getSyncTrie.bind(this);
    }

    async clear(userId: number, envDbVersion: number) {
        await this.ngZone.runOutsideAngular(async () => {
            await super.clear(userId, envDbVersion);
            localStorage.removeItem(`sync-trie+${dbVersion}+${userId}`);
            window.location.reload();
        });
    }

    async dropStaleDatabases(user, envDbVersion: number) {
        for (let i = 1; i < dbVersion; i++) {
            try {
                await deleteDB(this.getDBName(user, i));
            } catch (err) {
                logger.log(err);
            }
            for (let j = 1; j < envDbVersion; j++) {
                try {
                    await deleteDB(this.getDBName(user, i, j));
                } catch (err) {
                    logger.log(err);
                }
            }
            localStorage.removeItem(`sync-trie+${i}+${user.id}`);
        }
        localStorage.removeItem(`sync-trie+${user.id}`);
        logger.log('Removed staled databases');
    }

    async getAllDb(kind: 'docs'): Promise<IccDocument[]>;
    async getAllDb(kind: 'positions'): Promise<IccDocumentPosition[]>;
    async getAllDb(kind: 'appointments'): Promise<ComplaintAppointment[]>;
    async getAllDb(kind: 'complaint_notes'): Promise<ComplaintNote[]>;
    async getAllDb(kind: 'complaint_spare_parts'): Promise<ComplaintSparePart[]>;
    async getAllDb(kind: 'ops'): Promise<Operation[]>;
    async getAllDb(kind: 'document_connections'): Promise<SalesDocumentConnection[]>;
    async getAllDb(kind: 'attachments'): Promise<DocumentAttachment[]>;
    async getAllDb(kind: 'operation_graphs'): Promise<SalesDocumentConnections[]>;
    async getAllDb(kind: 'deliveries'): Promise<Delivery[]>;
    async getAllDb(kind: 'opened_documents'): Promise<OpenedDocument[]>;
    async getAllDb(
        kind:
            | 'docs'
            | 'positions'
            | 'appointments'
            | 'complaint_notes'
            | 'complaint_spare_parts'
            | 'ops'
            | 'document_connections'
            | 'attachments'
            | 'operation_graphs'
            | 'deliveries'
            | 'opened_documents'
    ): Promise<any[]> {
        return this.ngZone.runOutsideAngular(async () => super.getAllDb(kind));
    }

    async getManyFromIndexDb(kind: 'docs', index: string, ids: string[]): Promise<SalesDocument[]>;
    async getManyFromIndexDb(
        kind: 'positions',
        index: string,
        ids: string[]
    ): Promise<SalesDocumentPosition[]>;
    async getManyFromIndexDb(
        kind: 'appointments',
        index: string,
        ids: string[]
    ): Promise<ComplaintAppointment[]>;
    async getManyFromIndexDb(
        kind: 'complaint_notes',
        index: string,
        ids: string[]
    ): Promise<ComplaintNote[]>;
    async getManyFromIndexDb(
        kind: 'complaint_spare_parts',
        index: string,
        ids: string[]
    ): Promise<ComplaintSparePart[]>;
    async getManyFromIndexDb(kind: 'ops', index: string, ids: string[]): Promise<Operation[]>;
    async getManyFromIndexDb(
        kind: 'document_connections',
        index: string,
        ids: string[]
    ): Promise<SalesDocumentConnection[]>;
    async getManyFromIndexDb(
        kind: 'attachments',
        index: string,
        ids: string[]
    ): Promise<DocumentAttachment[]>;
    async getManyFromIndexDb(
        kind: 'operation_graphs',
        index: string,
        ids: string[]
    ): Promise<SalesDocumentConnections[]>;
    async getManyFromIndexDb(kind: 'deliveries', index: string, ids: string[]): Promise<Delivery[]>;
    async getManyFromIndexDb(
        kind: 'opened_documents',
        index: string,
        ids: string[]
    ): Promise<OpenedDocument[]>;
    async getManyFromIndexDb(
        kind:
            | 'docs'
            | 'positions'
            | 'appointments'
            | 'complaint_notes'
            | 'complaint_spare_parts'
            | 'ops'
            | 'document_connections'
            | 'attachments'
            | 'operation_graphs'
            | 'deliveries'
            | 'opened_documents',
        index: string,
        ids: string[]
    ): Promise<any[]> {
        return this.ngZone.runOutsideAngular(async () =>
            super.getManyFromIndexDb(kind, index, ids)
        );
    }

    async getMany(ids: string[], pairs: string[][] | null = null, minTimestamp = '') {
        return this.ngZone.runOutsideAngular(async () =>
            super.getMany(ids, pairs, minTimestamp)
        );
    }

    async getAllKeysDb(kind:
        | 'docs'
        | 'positions'
        | 'appointments'
        | 'complaint_notes'
        | 'complaint_spare_parts'
        | 'ops'
        | 'document_connections'
        | 'attachments'
        | 'operation_graphs'
        | 'deliveries'
        | 'opened_documents',
        index?: string,
    ) {
        return this.ngZone.runOutsideAngular(async () => super.getAllKeysDb(kind, index));
    }

    async getInitialState(openedDocumentId: string) {
        return this.ngZone.runOutsideAngular(async () =>
            super.getInitialState(openedDocumentId)
        );
    }

    async getDocumentsByRootId(rootIds: string[]) {
        return this.ngZone.runOutsideAngular(async () =>
            super.getDocumentsByRootId(rootIds)
        );
    }

    async getManyOps(ids: string[], minTimestamp = '') {
        return this.ngZone.runOutsideAngular(async () => super.getManyOps(ids, minTimestamp));
    }

    async saveMany(kind: 'docs', docs: IccDocument[]): Promise<void>;
    async saveMany(kind: 'positions', docs: IccDocumentPosition[]): Promise<void>;
    async saveMany(kind: 'appointments', docs: ComplaintAppointment[]): Promise<void>;
    async saveMany(kind: 'complaint_notes', docs: ComplaintNote[]): Promise<void>;
    async saveMany(kind: 'complaint_spare_parts', docs: ComplaintSparePart[]): Promise<void>;
    async saveMany(kind: 'ops', docs: Operation[]): Promise<void>;
    async saveMany(kind: 'document_connections', docs: SalesDocumentConnection[]): Promise<void>;
    async saveMany(kind: 'attachments', docs: DocumentAttachment[]): Promise<void>;
    async saveMany(kind: 'deliveries', docs: Delivery[]): Promise<void>;
    async saveMany(kind: 'opened_documents', docs: OpenedDocument[]): Promise<void>;
    async saveMany(
        kind: 'docs' | 'positions' | 'appointments' | 'complaint_notes' | 'complaint_spare_parts' | 'ops' | 'document_connections' | 'attachments' | 'deliveries' | 'opened_documents',
        docs: any[]
    ): Promise<void> {
        await this.ngZone.runOutsideAngular(async () => super.saveMany(kind, docs));
    }

    async deleteMany(kind: 'docs', docs: IccDocument['uuid'][]): Promise<void>;
    async deleteMany(kind: 'positions', docs: IccDocumentPosition['uuid'][]): Promise<void>;
    async deleteMany(kind: 'appointments', docs: ComplaintAppointment['uuid'][]): Promise<void>;
    async deleteMany(kind: 'complaint_notes', docs: ComplaintNote['uuid'][]): Promise<void>;
    async deleteMany(kind: 'complaint_spare_parts', docs: ComplaintSparePart['uuid'][]): Promise<void>;
    async deleteMany(kind: 'ops', docs: Operation['id'][]): Promise<void>;
    async deleteMany(
        kind: 'document_connections',
        docs: SalesDocumentConnection['uuid'][]
    ): Promise<void>;
    async deleteMany(kind: 'attachments', docs: DocumentAttachment['uuid'][]): Promise<void>;
    async deleteMany(kind: 'deliveries', docs: Delivery['uuid'][]): Promise<void>;
    async deleteMany(kind: 'opened_documents', docs: OpenedDocument['uuid'][]): Promise<void>;
    async deleteMany(
        kind: 'docs' | 'positions' | 'appointments' | 'complaint_notes' | 'complaint_spare_parts' | 'ops' | 'document_connections' | 'attachments' | 'deliveries' | 'opened_documents',
        docs: any[]
    ): Promise<void> {
        await this.ngZone.runOutsideAngular(async () => {
            await super.deleteMany(kind, docs);
        });
    }
}
